import { Link, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { formatRelativeTime, getNotificationIcon, notificationTarget } from "../../../utils";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import type { WsNotificationPayload } from "@complete-athlete/backend";

type NotificationPreviewProps = {
  notification: WsNotificationPayload;
  onMarkRead: (id: string) => void;
};

const activitySearch = { tab: "activity" as const };

export function NotificationPreview({ notification, onMarkRead }: NotificationPreviewProps) {
  const isUnread = !notification.readAt;
  const Icon = getNotificationIcon(notification.type);
  const target = notificationTarget(notification);
  const navigate = useNavigate();
  const titleClassName = cn(
    "text-[13px] leading-5 line-clamp-2 hover:underline",
    isUnread ? "font-medium text-foreground" : "font-normal text-foreground/80",
  );

  return (
    <div
      data-unread={isUnread || undefined}
      className={cn(
        "group flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition-colors",
        isUnread ? "bg-accent/60 hover:bg-accent/80" : "hover:bg-accent/30",
      )}
    >
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-foreground/10 text-foreground/75 ring-1 ring-inset ring-foreground/10">
        <Icon className="h-3.5 w-3.5" />
      </div>

      <div className="min-w-0 flex-1">
        {target ? (
          <a
            href={target.kind === "here" ? target.href : target.url}
            className={titleClassName}
            onClick={(event) => {
              if (isUnread) onMarkRead(notification.id);
              if (target.kind !== "here") return;
              event.preventDefault();
              void navigate({ href: target.href });
            }}
          >
            {notification.title}
          </a>
        ) : (
          <Link to="/notifications" search={activitySearch} className={titleClassName}>
            {notification.title}
          </Link>
        )}
        {notification.body && (
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground line-clamp-2">{notification.body}</p>
        )}
        <div className="mt-1 block text-[10px] text-muted-foreground">{formatRelativeTime(notification.createdAt)}</div>
      </div>

      {isUnread && (
        <div className="mt-0.5 shrink-0">
          <button
            type="button"
            aria-label={m.notifications_mark_as_read()}
            className="relative flex size-7 cursor-pointer items-center justify-center rounded-md outline-offset-2 hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
            onClick={() => onMarkRead(notification.id)}
            title={m.notifications_mark_as_read()}
          >
            <span
              aria-hidden
              className="absolute size-2 rounded-full bg-destructive group-hover:opacity-0 group-focus-within:opacity-0"
            />
            <Check className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" />
          </button>
        </div>
      )}
    </div>
  );
}
